import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useEventDrilldown } from '../hooks/useEventDrilldown';
import { getPillLabels } from '../utils/getPillLabels';
import { AgendaPanel } from '../components/AgendaPanel';
import { OperationsRolesPanel } from '../components/OperationsRolesPanel';
import { GateMonitoringView } from '../components/gate/GateMonitoringView';
import { EntranceManagement } from '../components/gate/EntranceManagement';
import { GuestPassPanel } from '../components/event-drilldown/GuestPassPanel';
import { useAuth } from '../contexts/AuthContext';
import { isAtLeast } from '../utils/roles';
import { apiClient } from '../api/client';
import { useMediaQuery } from '../hooks/useMediaQuery';
import {
  PRIMARY,
  TEXT_MUTED,
  BORDER_LIGHT,
  BG_PAGE,
  BG_CARD,
  BG_SURFACE,
  TEXT_DARK,
  FONT_HEADING,
} from '../components/legacy-ball/table-management/designTokens';

// ─── Types ───────────────────────────────────────────────────────────────────

type SubTab = 'overview' | 'vendors' | 'performers' | 'provisioning' | 'sponsors' | 'guest-pass' | 'gate-monitoring' | 'entrances';

const SUB_TABS: { key: SubTab; label: string; minRole?: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'vendors', label: 'Vendors' },
  { key: 'performers', label: 'Agenda / Performers' },
  { key: 'provisioning', label: 'Provisioning' },
  { key: 'sponsors', label: 'Sponsors' },
  { key: 'guest-pass', label: 'GuestPass', minRole: 'Delegate' },
  { key: 'gate-monitoring', label: 'Gate Monitoring', minRole: 'Delegate' },
  { key: 'entrances', label: 'Manage Entrances', minRole: 'Organizer' },
];

// ─── Formatting Helpers ──────────────────────────────────────────────────────

function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
  const [year, month, day] = datePart.split('-').map(Number);
  const d = new Date(year, month - 1, day);
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}

function formatTime(timeStr: string): string {
  if (!timeStr) return '';
  if (timeStr.includes('T')) {
    const [h, m] = timeStr.split('T')[1].slice(0, 5).split(':').map(Number);
    return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
  }
  if (timeStr.includes(':')) {
    const [h, m] = timeStr.split(':').map(Number);
    return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
  }
  return timeStr;
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  page: {
    backgroundColor: BG_PAGE,
    minHeight: '100vh',
    padding: '1.5rem',
  },
  backLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '0.85rem',
    color: PRIMARY,
    fontWeight: 500,
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    padding: '4px 0',
    marginBottom: '1rem',
  },
  header: {
    marginBottom: '1.25rem',
  },
  title: {
    fontFamily: FONT_HEADING,
    fontSize: '1.5rem',
    fontWeight: 700,
    color: TEXT_DARK,
    margin: '0 0 0.25rem 0',
  },
  subtitle: {
    fontSize: '0.85rem',
    color: TEXT_MUTED,
    margin: 0,
  },
  subNav: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap' as const,
    overflowX: 'auto' as const,
    borderBottom: `2px solid ${BORDER_LIGHT}`,
    marginBottom: '1.25rem',
  },
  subTab: {
    padding: '10px 16px',
    border: 'none',
    background: 'none',
    fontSize: '0.85rem',
    fontWeight: 500,
    color: TEXT_MUTED,
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
    borderBottom: '2px solid transparent',
    marginBottom: '-2px',
    minHeight: '44px',
    minWidth: '44px',
  },
  subTabActive: {
    padding: '10px 16px',
    border: 'none',
    background: 'none',
    fontSize: '0.85rem',
    fontWeight: 600,
    color: PRIMARY,
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
    borderBottom: `2px solid ${PRIMARY}`,
    marginBottom: '-2px',
    minHeight: '44px',
    minWidth: '44px',
  },
  spinner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4rem',
    fontSize: '1rem',
    color: TEXT_MUTED,
  },
  errorState: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4rem',
    gap: '1rem',
    textAlign: 'center' as const,
  },
  errorText: {
    color: '#d32f2f',
    fontSize: '0.9rem',
  },
  retryBtn: {
    padding: '10px 20px',
    backgroundColor: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: 600,
    minHeight: '44px',
    minWidth: '44px',
  },

  card: {
    backgroundColor: BG_CARD,
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '8px',
    padding: '1rem',
    marginBottom: '0.75rem',
  },
  placeholder: {
    padding: '2rem',
    textAlign: 'center' as const,
    color: TEXT_MUTED,
    fontSize: '0.85rem',
    backgroundColor: BG_SURFACE,
    borderRadius: '8px',
    border: `1px solid ${BORDER_LIGHT}`,
  },
  overviewGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '1rem',
  },
  overviewCard: {
    backgroundColor: BG_CARD,
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '8px',
    padding: '1.25rem',
    textAlign: 'center' as const,
  },
  overviewCount: {
    fontSize: '2rem',
    fontWeight: 700,
    color: TEXT_DARK,
    lineHeight: 1.2,
  },
  overviewLabel: {
    fontSize: '0.85rem',
    color: TEXT_MUTED,
    marginTop: '0.25rem',
  },
  infoCardsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: '1rem',
    marginTop: '1.5rem',
  },
  infoCard: {
    backgroundColor: BG_CARD,
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '8px',
    padding: '1.25rem',
  },
  infoCardTitle: {
    fontSize: '0.9rem',
    fontWeight: 600,
    color: TEXT_DARK,
    marginBottom: '0.75rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  infoCardText: {
    fontSize: '0.85rem',
    color: TEXT_DARK,
    lineHeight: 1.5,
  },
  infoCardMuted: {
    fontSize: '0.85rem',
    color: TEXT_MUTED,
    fontStyle: 'italic',
  },
};

// ─── Vendor Types ────────────────────────────────────────────────────────────

interface Vendor {
  id: string;
  businessName: string;
  contactPerson: string;
  phone: string | null;
  category: string | null;
  boothNumber: string | null;
  zone: string | null;
}

interface VendorGroup {
  category: string;
  vendors: Vendor[];
}

// ─── DrilldownVendorsTab ─────────────────────────────────────────────────────

function groupVendorsByCategory(vendors: Vendor[]): VendorGroup[] {
  const groups: Record<string, Vendor[]> = {};
  for (const vendor of vendors) {
    const cat = vendor.category || 'Uncategorized';
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(vendor);
  }

  // Sort groups alphabetically by category name (case-insensitive)
  const sortedKeys = Object.keys(groups).sort((a, b) =>
    a.toLowerCase().localeCompare(b.toLowerCase()),
  );

  return sortedKeys.map((category) => ({
    category,
    // Sort vendors within each group alphabetically by businessName
    vendors: groups[category].sort((a, b) =>
      a.businessName.toLowerCase().localeCompare(b.businessName.toLowerCase()),
    ),
  }));
}

const vendorStyles: Record<string, React.CSSProperties> = {
  categoryHeading: {
    fontSize: '0.95rem',
    fontWeight: 600,
    color: TEXT_DARK,
    marginBottom: '0.5rem',
    marginTop: '1rem',
  },
  categoryCount: {
    fontWeight: 400,
    color: TEXT_MUTED,
    fontSize: '0.85rem',
    marginLeft: '0.5rem',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    marginBottom: '1rem',
  },
  th: {
    textAlign: 'left' as const,
    padding: '8px 10px',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: TEXT_MUTED,
    borderBottom: `1px solid ${BORDER_LIGHT}`,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
  },
  td: {
    padding: '8px 10px',
    fontSize: '0.85rem',
    color: TEXT_DARK,
    borderBottom: `1px solid ${BORDER_LIGHT}`,
  },
  unassigned: {
    color: TEXT_MUTED,
    fontStyle: 'italic' as const,
  },
};

function DrilldownVendorsTab({ vendors }: { vendors: Vendor[] }) {
  if (vendors.length === 0) {
    return (
      <div style={styles.placeholder}>No vendors are assigned to this event.</div>
    );
  }

  const groups = groupVendorsByCategory(vendors);

  return (
    <div>
      {groups.map((group) => (
        <div key={group.category}>
          <div style={vendorStyles.categoryHeading}>
            {group.category}
            <span style={vendorStyles.categoryCount}>({group.vendors.length})</span>
          </div>
          <table style={vendorStyles.table}>
            <thead>
              <tr>
                <th style={vendorStyles.th}>Business Name</th>
                <th style={vendorStyles.th}>Contact</th>
                <th style={vendorStyles.th}>Phone</th>
                <th style={vendorStyles.th}>Booth</th>
                <th style={vendorStyles.th}>Zone</th>
              </tr>
            </thead>
            <tbody>
              {group.vendors.map((vendor) => (
                <tr key={vendor.id}>
                  <td style={vendorStyles.td}>{vendor.businessName}</td>
                  <td style={vendorStyles.td}>{vendor.contactPerson}</td>
                  <td style={vendorStyles.td}>{vendor.phone || '—'}</td>
                  <td style={vendorStyles.td}>
                    {vendor.boothNumber ? (
                      vendor.boothNumber
                    ) : (
                      <span style={vendorStyles.unassigned}>Unassigned</span>
                    )}
                  </td>
                  <td style={vendorStyles.td}>
                    {vendor.zone ? (
                      vendor.zone
                    ) : (
                      <span style={vendorStyles.unassigned}>Unassigned</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

// ─── Sponsor Types ───────────────────────────────────────────────────────────

interface Sponsor {
  id: string;
  sponsorName: string;
  contactPerson: string;
  phone: string | null;
  tier: string;
  pledgedAmount: number;
}

interface SponsorGroup {
  tier: string;
  sponsors: Sponsor[];
}

const TIER_ORDER = ['Platinum', 'Gold', 'Silver', 'Bronze', 'Custom'];

const TIER_COLORS: Record<string, string> = {
  Platinum: '#7c3aed',
  Gold: '#d97706',
  Silver: '#6b7280',
  Bronze: '#92400e',
  Custom: '#0d9488',
};

function groupSponsorsByTier(sponsors: Sponsor[]): SponsorGroup[] {
  const groups: Record<string, Sponsor[]> = {};
  for (const sponsor of sponsors) {
    const tier = sponsor.tier || 'Custom';
    if (!groups[tier]) groups[tier] = [];
    groups[tier].push(sponsor);
  }

  // Sort within each tier alphabetically by sponsorName
  for (const tier of Object.keys(groups)) {
    groups[tier].sort((a, b) =>
      a.sponsorName.toLowerCase().localeCompare(b.sponsorName.toLowerCase()),
    );
  }

  // Return groups in fixed tier order, only including tiers that have sponsors
  return TIER_ORDER.filter((tier) => groups[tier] && groups[tier].length > 0).map(
    (tier) => ({ tier, sponsors: groups[tier] }),
  );
}

const sponsorStyles: Record<string, React.CSSProperties> = {
  tierHeading: {
    fontSize: '0.95rem',
    fontWeight: 600,
    color: TEXT_DARK,
    marginBottom: '0.5rem',
    marginTop: '1rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  tierBadge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '12px',
    fontSize: '0.7rem',
    fontWeight: 600,
    color: '#fff',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
  },
  tierCount: {
    fontWeight: 400,
    color: TEXT_MUTED,
    fontSize: '0.85rem',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    marginBottom: '1rem',
  },
  th: {
    textAlign: 'left' as const,
    padding: '8px 10px',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: TEXT_MUTED,
    borderBottom: `1px solid ${BORDER_LIGHT}`,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
  },
  td: {
    padding: '8px 10px',
    fontSize: '0.85rem',
    color: TEXT_DARK,
    borderBottom: `1px solid ${BORDER_LIGHT}`,
  },
  naText: {
    color: TEXT_MUTED,
    fontStyle: 'italic' as const,
  },
};

function DrilldownSponsorsTab({ sponsors }: { sponsors: Sponsor[] }) {
  if (sponsors.length === 0) {
    return (
      <div style={styles.placeholder}>No sponsors are linked to this event.</div>
    );
  }

  const groups = groupSponsorsByTier(sponsors);

  return (
    <div>
      {groups.map((group) => (
        <div key={group.tier}>
          <div style={sponsorStyles.tierHeading}>
            <span
              style={{
                ...sponsorStyles.tierBadge,
                backgroundColor: TIER_COLORS[group.tier] || TIER_COLORS.Custom,
              }}
            >
              {group.tier}
            </span>
            <span style={sponsorStyles.tierCount}>({group.sponsors.length})</span>
          </div>
          <table style={sponsorStyles.table}>
            <thead>
              <tr>
                <th style={sponsorStyles.th}>Sponsor Name</th>
                <th style={sponsorStyles.th}>Contact Person</th>
                <th style={sponsorStyles.th}>Phone</th>
                <th style={sponsorStyles.th}>Tier</th>
                <th style={sponsorStyles.th}>Pledged Amount</th>
              </tr>
            </thead>
            <tbody>
              {group.sponsors.map((sponsor) => (
                <tr key={sponsor.id}>
                  <td style={sponsorStyles.td}>{sponsor.sponsorName}</td>
                  <td style={sponsorStyles.td}>{sponsor.contactPerson}</td>
                  <td style={sponsorStyles.td}>
                    {sponsor.phone ? (
                      sponsor.phone
                    ) : (
                      <span style={sponsorStyles.naText}>N/A</span>
                    )}
                  </td>
                  <td style={sponsorStyles.td}>{sponsor.tier}</td>
                  <td style={sponsorStyles.td}>
                    ${sponsor.pledgedAmount.toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

// ─── Provisioning Types ──────────────────────────────────────────────────────

interface Provisioning {
  registrationCount: number;
  venueCapacity: number;
  capacityUtilizationPercent: number;
  venueName: string;
  venueAddress: string;
  mealBreakdown: { optionName: string; count: number; vipCount: number; patronCount: number; accessibilityCount: number }[] | null;
  guestHighlights?: { totalVip: number; totalPatrons: number; totalAccessibility: number };
}

// ─── DrilldownProvisioningTab ────────────────────────────────────────────────

const provisioningStyles: Record<string, React.CSSProperties> = {
  section: {
    backgroundColor: BG_CARD,
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '8px',
    padding: '1.25rem',
    marginBottom: '1rem',
  },
  sectionTitle: {
    fontSize: '0.9rem',
    fontWeight: 600,
    color: TEXT_DARK,
    marginBottom: '0.75rem',
  },
  capacityRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '0.5rem',
    marginBottom: '0.5rem',
  },
  capacityValue: {
    fontSize: '1.25rem',
    fontWeight: 700,
    color: TEXT_DARK,
  },
  capacityLabel: {
    fontSize: '0.85rem',
    color: TEXT_MUTED,
  },
  progressBarOuter: {
    width: '100%',
    height: '8px',
    backgroundColor: BORDER_LIGHT,
    borderRadius: '4px',
    overflow: 'hidden' as const,
  },
  progressBarInner: {
    height: '100%',
    backgroundColor: PRIMARY,
    borderRadius: '4px',
    transition: 'width 0.3s ease',
  },
  venueRow: {
    fontSize: '0.85rem',
    color: TEXT_DARK,
    marginBottom: '0.25rem',
  },
  venueAddress: {
    fontSize: '0.85rem',
    color: TEXT_MUTED,
  },
  mealList: {
    listStyle: 'none' as const,
    padding: 0,
    margin: 0,
  },
  mealItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '6px 0',
    borderBottom: `1px solid ${BORDER_LIGHT}`,
    fontSize: '0.85rem',
  },
  mealName: {
    color: TEXT_DARK,
  },
  mealCount: {
    fontWeight: 600,
    color: TEXT_DARK,
  },
  noMeals: {
    fontSize: '0.85rem',
    color: TEXT_MUTED,
    fontStyle: 'italic' as const,
  },
};

function DrilldownProvisioningTab({ provisioning }: { provisioning: Provisioning }) {
  const { registrationCount, venueCapacity, capacityUtilizationPercent, venueName, venueAddress, mealBreakdown } = provisioning;
  const progressWidth = Math.min(capacityUtilizationPercent, 100);

  return (
    <div>
      {/* Capacity Section */}
      <div style={provisioningStyles.section}>
        <div style={provisioningStyles.sectionTitle}>Capacity</div>
        <div style={provisioningStyles.capacityRow}>
          <span style={provisioningStyles.capacityValue}>
            {registrationCount} / {venueCapacity}
          </span>
          <span style={provisioningStyles.capacityLabel}>
            ({capacityUtilizationPercent}%)
          </span>
        </div>
        <div style={provisioningStyles.progressBarOuter}>
          <div
            style={{ ...provisioningStyles.progressBarInner, width: `${progressWidth}%` }}
            role="progressbar"
            aria-valuenow={capacityUtilizationPercent}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`Capacity utilization: ${capacityUtilizationPercent}%`}
          />
        </div>
      </div>

      {/* Venue Section */}
      <div style={provisioningStyles.section}>
        <div style={provisioningStyles.sectionTitle}>Venue</div>
        <div style={provisioningStyles.venueRow}>{venueName}</div>
        <div style={provisioningStyles.venueAddress}>{venueAddress}</div>
      </div>

      {/* Meal Breakdown Section */}
      <div style={provisioningStyles.section}>
        <div style={provisioningStyles.sectionTitle}>Meal Selections</div>
        {mealBreakdown && mealBreakdown.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${BORDER_LIGHT}` }}>
                  <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600, color: TEXT_DARK }}>Meal Option</th>
                  <th style={{ textAlign: 'center', padding: '6px 10px', fontWeight: 600, color: TEXT_DARK }}>Count</th>
                  <th style={{ textAlign: 'center', padding: '6px 10px', fontWeight: 600, color: '#f9a825' }}>VIP</th>
                  <th style={{ textAlign: 'center', padding: '6px 10px', fontWeight: 600, color: '#6a1b9a' }}>Patrons</th>
                  <th style={{ textAlign: 'center', padding: '6px 10px', fontWeight: 600, color: '#1565c0' }}>Accessibility</th>
                </tr>
              </thead>
              <tbody>
                {mealBreakdown.map((meal) => (
                  <tr key={meal.optionName} style={{ borderBottom: `1px solid ${BORDER_LIGHT}` }}>
                    <td style={{ padding: '6px 10px', color: TEXT_DARK }}>{meal.optionName}</td>
                    <td style={{ textAlign: 'center', padding: '6px 10px', fontWeight: 600, color: TEXT_DARK }}>{meal.count}</td>
                    <td style={{ textAlign: 'center', padding: '6px 10px', color: meal.vipCount > 0 ? '#f9a825' : TEXT_MUTED }}>{meal.vipCount}</td>
                    <td style={{ textAlign: 'center', padding: '6px 10px', color: meal.patronCount > 0 ? '#6a1b9a' : TEXT_MUTED }}>{meal.patronCount}</td>
                    <td style={{ textAlign: 'center', padding: '6px 10px', color: meal.accessibilityCount > 0 ? '#1565c0' : TEXT_MUTED }}>{meal.accessibilityCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={provisioningStyles.noMeals}>No meal selections have been recorded.</div>
        )}
      </div>
    </div>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function EventLogisticsDrilldownPage() {
  const { eventId: routeKey } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<SubTab>('overview');
  const { user } = useAuth();
  const isMobile = useMediaQuery(767);
  const navRef = useRef<HTMLElement>(null);

  // The URL param may be a readable slug or a UUID. Resolve to canonical id once.
  const [eventId, setEventId] = useState<string>('');
  useEffect(() => {
    let cancelled = false;
    if (!routeKey) { setEventId(''); return; }
    apiClient<{ id: string }>(`/events/resolve/${routeKey}`)
      .then((r) => { if (!cancelled) setEventId(r.id); })
      .catch(() => { if (!cancelled) setEventId(routeKey); });
    return () => { cancelled = true; };
  }, [routeKey]);

  const { data, loading, error, retry } = useEventDrilldown(eventId || '');

  // Track active session state for EntranceManagement
  const [hasActiveSession, setHasActiveSession] = useState(false);

  useEffect(() => {
    if (!eventId) return;
    apiClient<{ id: string; status: string }[]>(`/gate/events/${eventId}/sessions`)
      .then((sessions) => {
        setHasActiveSession(sessions.some((s) => s.status === 'Active'));
      })
      .catch(() => {
        // Silently fail — session status is non-critical
      });
  }, [eventId]);

  // Auto-scroll to active tab on mobile
  useEffect(() => {
    if (isMobile && navRef.current) {
      const activeBtn = navRef.current.querySelector('[aria-current="page"]');
      if (activeBtn) {
        (activeBtn as HTMLElement).scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }
    }
  }, [activeTab, isMobile]);

  // Mobile-conditional styles
  const pageStyle: React.CSSProperties = {
    ...styles.page,
    padding: isMobile ? '8px 12px' : '1.5rem',
  };

  const backLinkStyle: React.CSSProperties = isMobile
    ? { ...styles.backLink, minHeight: '44px', minWidth: '44px', padding: '10px 0' }
    : styles.backLink;

  const subNavStyle: React.CSSProperties = isMobile
    ? {
        ...styles.subNav,
        position: 'sticky',
        top: 0,
        zIndex: 10,
        backgroundColor: BG_PAGE,
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        flexWrap: 'nowrap',
      } as React.CSSProperties
    : styles.subNav;

  const subTabStyle = (isActive: boolean): React.CSSProperties => {
    const base = isActive ? styles.subTabActive : styles.subTab;
    return isMobile ? { ...base, minHeight: '44px', padding: '0 16px' } : base;
  };

  const overviewGridStyle: React.CSSProperties = isMobile
    ? { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }
    : styles.overviewGrid;

  const infoCardsGridStyle: React.CSSProperties = isMobile
    ? { display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '1.5rem' }
    : styles.infoCardsGrid;

  const touchTargetStyle: React.CSSProperties = isMobile
    ? { minHeight: '44px', minWidth: '44px' }
    : {};

  // Filter tabs based on user role
  const visibleTabs = SUB_TABS.filter((tab) => {
    if (!tab.minRole) return true;
    return user?.role && isAtLeast(user.role, tab.minRole);
  });

  // Loading state
  if (loading) {
    return (
      <div style={pageStyle}>
        <button
          style={backLinkStyle}
          onClick={() => navigate('/logistics-center')}
          aria-label="Back to K-LOC"
        >
          ← Back to K-LOC
        </button>
        <div style={styles.spinner}>Loading...</div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={pageStyle}>
        <button
          style={backLinkStyle}
          onClick={() => navigate('/logistics-center')}
          aria-label="Back to K-LOC"
        >
          ← Back to K-LOC
        </button>
        <div style={styles.errorState}>
          <div style={styles.errorText}>⚠️ {error.message}</div>
          {!error.notFound && (
            <button style={{ ...styles.retryBtn, ...touchTargetStyle }} onClick={retry} aria-label="Try again">
              Try Again
            </button>
          )}
        </div>
      </div>
    );
  }

  // No data (shouldn't happen if no error/loading, but guard)
  if (!data) {
    return (
      <div style={pageStyle}>
        <button
          style={backLinkStyle}
          onClick={() => navigate('/logistics-center')}
          aria-label="Back to K-LOC"
        >
          ← Back to K-LOC
        </button>
        <div style={styles.spinner}>No data available.</div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      {/* WebKit scrollbar hiding for mobile sub-nav */}
      {isMobile && (
        <style>{`.drilldown-mobile-subnav::-webkit-scrollbar { display: none; }`}</style>
      )}

      {/* Back navigation */}
      <button
        style={backLinkStyle}
        onClick={() => navigate('/logistics-center')}
        aria-label="Back to K-LOC"
      >
        ← Back to K-LOC
      </button>

      {/* Event header */}
      <div style={styles.header}>
        <h1 style={styles.title}>{data.event.name}</h1>
        <p style={styles.subtitle}>
          {formatDate(data.event.date)} · {formatTime(data.event.startTime)} – {formatTime(data.event.endTime)} · {data.event.venue.name}
        </p>
      </div>

      {/* Sub-tab navigation */}
      <nav
        ref={navRef}
        style={subNavStyle}
        className={isMobile ? 'drilldown-mobile-subnav' : undefined}
        aria-label="Event logistics sections"
      >
        <div style={{ display: 'flex', gap: '0' }}>
          {visibleTabs.map((tab) => (
            <button
              key={tab.key}
              style={subTabStyle(activeTab === tab.key)}
              onClick={() => setActiveTab(tab.key)}
              aria-current={activeTab === tab.key ? 'page' : undefined}
            >
              {tab.label}
            </button>
          ))}
        </div>
        {data?.provisioning?.guestHighlights && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '0.8rem', color: '#6b7280' }}>
            <span>⭐ {data.provisioning.guestHighlights.totalVip} VIP</span>
            <span>💎 {data.provisioning.guestHighlights.totalPatrons} Patrons</span>
            <span>♿ {data.provisioning.guestHighlights.totalAccessibility} Accessibility</span>
          </div>
        )}
      </nav>

      {/* Tab content */}
      {activeTab === 'overview' && (
        <>
          {(() => {
            const pills = getPillLabels(data.isLegacyBall);
            return (
              <div style={overviewGridStyle}>
                <div style={styles.overviewCard}>
                  <div style={styles.overviewCount}>{data.summary.vendorCount}</div>
                  <div style={styles.overviewLabel}>🏪 Vendors</div>
                </div>
                <div style={styles.overviewCard}>
                  <div style={styles.overviewCount}>{data.summary.performerCount}</div>
                  <div style={styles.overviewLabel}>🎭 Performers</div>
                </div>
                <div style={styles.overviewCard}>
                  <div style={styles.overviewCount}>{data.summary.registrationCount}</div>
                  <div style={styles.overviewLabel}>{pills.registrations.icon} {pills.registrations.label}</div>
                </div>
                <div style={{ ...styles.overviewCard, borderLeft: '3px solid #4caf50' }}>
                  <div style={styles.overviewCount}>{data.summary.checkedInCount}</div>
                  <div style={styles.overviewLabel}>{pills.checkedIn.icon} {pills.checkedIn.label}</div>
                </div>
                <div style={{ ...styles.overviewCard, borderLeft: '3px solid #ff9800' }}>
                  <div style={styles.overviewCount}>{data.summary.notCheckedInCount}</div>
                  <div style={styles.overviewLabel}>{pills.notCheckedIn.icon} {pills.notCheckedIn.label}</div>
                </div>
                <div style={{ ...styles.overviewCard, borderLeft: '3px solid #1565c0' }}>
                  <div style={styles.overviewCount}>{data.summary.totalExpected}</div>
                  <div style={styles.overviewLabel}>{pills.totalExpected.icon} {pills.totalExpected.label}</div>
                </div>
                <div style={styles.overviewCard}>
                  <div style={styles.overviewCount}>{data.summary.sponsorCount}</div>
                  <div style={styles.overviewLabel}>💎 Sponsors</div>
                </div>
              </div>
            );
          })()}

          <div className="panels-grid">
            <AgendaPanel performers={data.performers} />
            <OperationsRolesPanel
              eventId={eventId || ''}
              operationsRoles={data.operationsRoles ?? []}
              loading={loading}
              error={!!error}
              onRetry={retry}
              onRoleUpdated={retry}
            />
          </div>

          {/* Info Cards Grid */}
          <div style={infoCardsGridStyle}>
            {/* Vendor Contact Card */}
            <div style={styles.infoCard}>
              <div style={styles.infoCardTitle}>📞 Vendor Contact</div>
              {data.event.venue.contactName || data.event.venue.contactPhone || data.event.venue.contactEmail ? (
                <div style={styles.infoCardText}>
                  {data.event.venue.contactName && <div>{data.event.venue.contactName}</div>}
                  {data.event.venue.contactPhone && <div>{data.event.venue.contactPhone}</div>}
                  {data.event.venue.contactEmail && <div>{data.event.venue.contactEmail}</div>}
                </div>
              ) : (
                <div style={styles.infoCardMuted}>No contact information available.</div>
              )}
            </div>

            {/* Parking Details Card */}
            <div style={styles.infoCard}>
              <div style={styles.infoCardTitle}>🅿️ Parking Details</div>
              {data.event.venue.parkingInfo ? (
                <div style={styles.infoCardText}>{data.event.venue.parkingInfo}</div>
              ) : (
                <div style={styles.infoCardMuted}>No parking details available.</div>
              )}
            </div>

            {/* Meal Summary Card */}
            <div style={styles.infoCard}>
              <div style={styles.infoCardTitle}>🍽️ Meal Summary</div>
              {data.provisioning.mealBreakdown && data.provisioning.mealBreakdown.length > 0 ? (
                <div style={styles.infoCardText}>
                  {data.provisioning.mealBreakdown.map((meal) => (
                    <div key={meal.optionName}>{meal.optionName}: {meal.count}</div>
                  ))}
                </div>
              ) : data.event.mealInfo ? (
                <div style={styles.infoCardText}>{data.event.mealInfo}</div>
              ) : (
                <div style={styles.infoCardMuted}>No meal information available.</div>
              )}
            </div>
          </div>
        </>
      )}
      {activeTab === 'vendors' && <DrilldownVendorsTab vendors={data.vendors} />}
      {activeTab === 'performers' && (
        (() => {
          const performers = data.performers;
          if (performers.length === 0) {
            return (
              <div style={styles.placeholder}>No performers are scheduled for this event.</div>
            );
          }
          return (
            <div>
              {performers.map((performer) => (
                <div key={performer.id} style={styles.card}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontWeight: 600, color: TEXT_DARK, fontSize: '0.95rem', marginBottom: '0.25rem' }}>
                        {performer.title}
                      </div>
                      {performer.speaker && (
                        <div style={{ fontSize: '0.85rem', color: TEXT_MUTED }}>
                          🎤 {performer.speaker}
                        </div>
                      )}
                    </div>
                    <div style={{ textAlign: 'right' as const, fontSize: '0.85rem', color: TEXT_MUTED, whiteSpace: 'nowrap' as const }}>
                      {formatTime(performer.startTime)} – {formatTime(performer.endTime)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          );
        })()
      )}
      {activeTab === 'provisioning' && (
        <DrilldownProvisioningTab provisioning={data.provisioning} />
      )}
      {activeTab === 'sponsors' && <DrilldownSponsorsTab sponsors={data.sponsors} />}
      {activeTab === 'gate-monitoring' && eventId && (
        <GateMonitoringView eventId={eventId} />
      )}
      {activeTab === 'entrances' && eventId && (
        <EntranceManagement eventId={eventId} hasActiveSession={hasActiveSession} />
      )}
      {activeTab === 'guest-pass' && eventId && (
        <GuestPassPanel eventId={eventId} />
      )}
    </div>
  );
}
